import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, XClose } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DialogTrigger, Popover } from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { useMultiValueSensors } from '@/lib/crm/utils/multiValueDnd';
import { MAX_MULTI_VALUE_ITEMS } from '@/lib/crm/utils/multiValueValidation';

import { PropertyInput } from '../PropertyInput';

type RowData = Record<string, unknown> & { _id?: string };

export interface CompositeMultiValueEditorProps {
  propertyType: PropertyTypeSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  trailingAddon?: React.ReactNode;
}

/**
 * Multi-value editor for composite properties with subproperties.
 * Each row renders a group of PropertyInput components — one per writable subproperty.
 */
export function CompositeMultiValueEditor({
  propertyType,
  value,
  onChange,
  disabled,
  trailingAddon,
}: CompositeMultiValueEditorProps) {
  const popoverZIndex = useStackingZIndex();
  const [announcement, setAnnouncement] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ids, setIds] = useState<string[]>(() => {
    // Initialize IDs from initial value array to avoid undefined IDs on first render
    const initialArray = Array.isArray(value) ? value : [];
    return initialArray.map(() => crypto.randomUUID());
  });
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Maintain a parallel array of IDs that tracks rows, not positions
  // Stabilize: prevents infinite re-renders from unstable fallback array identity
  const stableValueArray = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  // Sync ID array length with value array - stored in state to trigger re-renders
  useEffect(() => {
    if (ids.length < stableValueArray.length) {
      // New rows added - generate IDs for them
      const newIds = Array.from(
        { length: stableValueArray.length - ids.length },
        () => crypto.randomUUID()
      );
      setIds([...ids, ...newIds]);
    } else if (ids.length > stableValueArray.length) {
      // Rows removed - but we don't know which ones yet (handled in remove handler)
      // This case shouldn't happen because handlers maintain ID array sync
    }
  }, [stableValueArray.length, ids]);

  // Attach IDs to rows for React keys and DnD
  const valueArrayWithIds = stableValueArray.map((row, idx) => {
    if (typeof row === 'object' && row !== null) {
      return { ...row, _id: ids[idx] };
    }
    return row;
  });

  // Get user-facing subproperties (formula/computed subprops have value_read_access: internal)
  const writableSubprops = (propertyType.subproperties ?? []).filter(
    sp => sp.value_read_access === 'all'
  );

  // DnD sensors setup (shared configuration)
  const sensors = useMultiValueSensors();

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  // Drag handler
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = valueArrayWithIds.findIndex(
        (row: RowData) => row._id === active.id
      );
      const newIndex = valueArrayWithIds.findIndex(
        (row: RowData) => row._id === over.id
      );
      const newArray = arrayMove(valueArrayWithIds, oldIndex, newIndex);
      // Reorder ID array to match
      setIds(arrayMove(ids, oldIndex, newIndex));
      // Remove _id field before passing to onChange
      const cleanArray = newArray.map(({ _id, ...rest }: RowData) => rest);
      onChange([...cleanArray]);
      setAnnouncement(`Item moved to position ${newIndex + 1}`);
    }
  };

  // Add new row
  const handleAdd = useCallback(() => {
    // Enforce item limit (backend MAX_MULTI_VALUE_ITEMS)
    if (stableValueArray.length >= MAX_MULTI_VALUE_ITEMS) {
      setErrorMessage(
        `Maximum of ${MAX_MULTI_VALUE_ITEMS} values reached. Cannot add more.`
      );
      setAnnouncement(
        `Maximum of ${MAX_MULTI_VALUE_ITEMS} values reached. Cannot add more.`
      );
      return;
    }
    const newRow: Record<string, unknown> = {};
    writableSubprops.forEach(sp => {
      newRow[sp.subproperty_name] = null;
    });
    // Add new ID to parallel array
    setIds([...ids, crypto.randomUUID()]);
    // Don't include _id in the saved value
    onChange([...stableValueArray, newRow]);
    setErrorMessage(null);
    setAnnouncement(`Item added. ${stableValueArray.length + 1} items total.`);
  }, [stableValueArray, onChange, writableSubprops, ids]);

  // Remove row by ID
  const handleRemove = useCallback(
    (rowId: string) => {
      const index = valueArrayWithIds.findIndex(
        (row: RowData) => row._id === rowId
      );
      if (index !== -1) {
        const newArray = stableValueArray.filter((_, i) => i !== index);
        // Remove corresponding ID from parallel array
        setIds(ids.filter((_, i) => i !== index));
        onChange([...newArray]);
        setAnnouncement(`Item removed. ${newArray.length} items remaining.`);
      }
    },
    [stableValueArray, valueArrayWithIds, onChange, ids]
  );

  // Update subproperty value by row ID
  const handleSubpropertyChange = useCallback(
    (rowId: string, subpropertyName: string, newVal: unknown) => {
      const index = valueArrayWithIds.findIndex(
        (row: RowData) => row._id === rowId
      );
      if (index !== -1) {
        const newArray = [...stableValueArray];
        const row = { ...(newArray[index] as Record<string, unknown>) };
        row[subpropertyName] = newVal;
        newArray[index] = row;
        onChange([...newArray]);
      }
    },
    [stableValueArray, valueArrayWithIds, onChange]
  );

  const triggerLabel = disabled
    ? `View ${propertyType.label} values: ${valueArrayWithIds.length} items`
    : `Edit ${propertyType.label} values: ${valueArrayWithIds.length} items`;

  // Get the field type icon
  const Icon = getPropertyTypeIconComponent(propertyType);

  // Auto-focus on open
  const handleOpenChange = (isOpen: boolean) => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }
    if (isOpen && !disabled) {
      focusTimeoutRef.current = setTimeout(() => {
        // Focus first input in first row if exists
      }, 0);
    }
  };

  // Get display summary for trigger button
  const getSummary = (row: unknown): string => {
    if (typeof row === 'object' && row !== null) {
      const dict = row as Record<string, unknown>;
      if ('display_name' in dict && dict.display_name) {
        return String(dict.display_name);
      }
      const keys = Object.keys(dict);
      if (keys.length > 0 && dict[keys[0]]) {
        return String(dict[keys[0]]);
      }
    }
    return '—';
  };

  return (
    <div className='flex items-center gap-2'>
      <DialogTrigger onOpenChange={handleOpenChange}>
        <Button
          aria-label={triggerLabel}
          className={`relative flex items-center w-full rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset px-3 py-2 pl-10 hover:bg-bg-secondary focus-visible:ring-2 focus-visible:ring-brand ${disabled ? 'cursor-not-allowed bg-disabled_subtle ring-disabled' : ''}`}
        >
          <Icon className='pointer-events-none absolute left-3 size-5 text-fg-quaternary' />
          <span className='flex-1 text-md text-left truncate text-primary'>
            {valueArrayWithIds.length > 0
              ? getSummary(valueArrayWithIds[0])
              : '\u00A0'}
          </span>
          {valueArrayWithIds.length > 1 && (
            <span className='ml-1 text-xs text-tertiary'>
              +{valueArrayWithIds.length - 1}
            </span>
          )}
        </Button>
        <Popover
          className='bg-bg-primary border border-border-primary rounded-lg shadow-lg p-4 min-w-[400px] max-w-[600px]'
          placement='bottom start'
          style={{ zIndex: popoverZIndex }}
        >
          <StackingLayer>
            <div className='space-y-3'>
              {/* Aria-live region */}
              <div aria-live='polite' className='sr-only'>
                {announcement}
              </div>

              {/* Empty state */}
              {valueArrayWithIds.length === 0 && (
                <div className='py-4 px-3 text-center border border-dashed border-border-secondary rounded-lg'>
                  <p className='text-sm text-tertiary'>
                    No values yet. Click Add to create a new entry.
                  </p>
                </div>
              )}

              {/* Values list (scrollable) */}
              <div className='max-h-[40vh] overflow-y-auto space-y-3'>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={valueArrayWithIds
                      .filter(
                        (row: RowData): row is RowData & { _id: string } =>
                          Boolean(row._id)
                      )
                      .map(row => row._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {valueArrayWithIds.map((row: RowData, index) => {
                      if (!row._id) {
                        return null;
                      }
                      return (
                        <SortableCompositeRow
                          key={row._id}
                          id={row._id}
                          row={row as Record<string, unknown>}
                          index={index}
                          writableSubprops={writableSubprops}
                          onSubpropertyChange={(subprop, val) =>
                            handleSubpropertyChange(
                              row._id as string,
                              subprop,
                              val
                            )
                          }
                          onRemove={() => handleRemove(row._id as string)}
                          disabled={disabled}
                          parentWriteAccess={propertyType.value_write_access}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </div>

              {/* Add button */}
              {!disabled &&
                propertyType.value_write_access !== 'internal' &&
                propertyType.value_write_access !== 'none' && (
                  <Button
                    type='button'
                    onPress={handleAdd}
                    className='flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-secondary hover:bg-bg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
                  >
                    <Plus className='w-5 h-5' />
                    <span className='text-sm'>Add {propertyType.label}</span>
                  </Button>
                )}

              {/* Error message */}
              {errorMessage && (
                <div className='text-sm text-error'>{errorMessage}</div>
              )}
            </div>
          </StackingLayer>
        </Popover>
      </DialogTrigger>
      {trailingAddon}
    </div>
  );
}

// =============================================================================
// SortableCompositeRow - Individual composite value row
// =============================================================================

interface SortableCompositeRowProps {
  id: string;
  row: Record<string, unknown>;
  index: number;
  writableSubprops: Array<{
    subproperty_name: string;
    field_type_name: string;
    label: string;
    options?: { value: string; label: string }[];
    value_write_access: 'all' | 'internal' | 'none';
    value_read_access: 'all' | 'internal';
  }>;
  onSubpropertyChange: (subpropertyName: string, value: unknown) => void;
  onRemove: () => void;
  disabled: boolean;
  parentWriteAccess: 'all' | 'internal' | 'none';
}

function SortableCompositeRow({
  id,
  row,
  index,
  writableSubprops,
  onSubpropertyChange,
  onRemove,
  disabled,
  parentWriteAccess,
}: SortableCompositeRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isRowDisabled =
    disabled ||
    parentWriteAccess === 'internal' ||
    parentWriteAccess === 'none';

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, backgroundColor: 'var(--color-bg-secondary)' }}
      className={`border border-border-secondary rounded-lg p-3 space-y-2 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className='flex items-start gap-2'>
        <button
          type='button'
          {...listeners}
          {...attributes}
          className='flex items-center justify-center touch-none rounded mt-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
          aria-label='Drag to reorder'
          style={{ visibility: isRowDisabled ? 'hidden' : 'visible' }}
        >
          <GripVertical className='w-4 h-4 text-tertiary' />
        </button>
        <div className='flex-1 space-y-2'>
          {writableSubprops.map(sp => {
            const subpropDisabled =
              isRowDisabled ||
              sp.value_write_access === 'internal' ||
              sp.value_write_access === 'none';
            return (
              <div key={sp.subproperty_name}>
                <label className='block text-sm font-medium text-secondary mb-1'>
                  {sp.label}
                </label>
                <PropertyInput
                  propertyType={{
                    property_type_name: sp.subproperty_name,
                    entity_type_name: '',
                    label: sp.label,
                    field_type_name: sp.field_type_name,
                    description: '',
                    is_required: false,
                    is_unique_value: false,
                    value_write_access: sp.value_write_access,
                    value_read_access: sp.value_read_access,
                    value_update_access: 'all',
                    options: sp.options,
                  }}
                  value={
                    row[sp.subproperty_name] as
                      | string
                      | number
                      | object
                      | null
                      | undefined
                  }
                  onChange={val =>
                    onSubpropertyChange(sp.subproperty_name, val)
                  }
                  disabled={subpropDisabled}
                />
              </div>
            );
          })}
        </div>
        <button
          type='button'
          onClick={onRemove}
          disabled={isRowDisabled}
          aria-label={`Remove item ${index + 1}`}
          className='flex items-center justify-center w-6 h-6 rounded mt-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand hover:text-error'
          style={{ visibility: isRowDisabled ? 'hidden' : 'visible' }}
        >
          <XClose className='w-4 h-4' />
        </button>
      </div>
    </div>
  );
}
